import React, { memo } from "react";
import {
    CheckCircle2, Loader2, Lightbulb,
    Wrench, Search, FileCode2, Terminal, Image as ImageIcon,
    FileText, Link2,
} from "lucide-react";

/**
 * Timeline "ao vivo" da execução — só é exibida enquanto a IA está
 * rodando. Após `progress: done`, o componente devolve null e o card
 * desaparece completamente, cedendo espaço para a resposta final.
 *
 * Cada item da timeline vira uma linha enxuta, sem card nem borda,
 * com um ícone que reflete o status atual (spinner para em curso,
 * check para concluído) e um texto curto.
 */
function AgentTimelineImpl({ timeline = [], running = false }) {
    if (!running) return null;
    if (!timeline || timeline.length === 0) return null;

    // Só mostra a timeline quando existe pelo menos uma etapa real (step).
    // Se a resposta da IA for direta (só `thinking`), não mostra nada —
    // o thinking é apenas a prévia textual da resposta e já aparece
    // na mensagem final do assistente.
    const hasSteps = timeline.some((x) => x.kind === "step");
    if (!hasSteps) return null;

    const lines = flatten(timeline);
    if (lines.length === 0) return null;

    return (
        <div className="my-2 space-y-1.5" data-testid="agent-timeline-live">
            {lines.map((ln, i) => (
                <Line key={i} ln={ln} />
            ))}
        </div>
    );
}

const AgentTimeline = memo(AgentTimelineImpl);
export default AgentTimeline;

function Line({ ln }) {
    const { Icon, color } = ln.tool
        ? parseToolLabel(ln.tool.label)
        : { Icon: Lightbulb, color: "#FBBF24" };

    const doing = ln.status === "doing";
    // Shimmer SÓ aparece junto com o spinner (status=doing). Etapas
    // já concluídas ou pendentes ficam com texto normal — caso contrário
    // a tela inteira "tremelica" depois que a IA já terminou.

    return (
        <div className="flex items-center gap-2.5 text-sm" data-testid="timeline-line">
            {doing ? (
                <Loader2 className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />
            ) : ln.status === "done" ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
            ) : (
                <Icon className="w-3.5 h-3.5 shrink-0" style={{ color }} />
            )}
            <span
                className={`truncate ${doing ? "text-shimmer font-medium" : "text-muted-foreground"}`}
            >
                {ln.text}
            </span>
        </div>
    );
}

/**
 * Converte o array timeline[] em linhas planas, preservando ordem
 * cronológica e detalhando tools dentro de cada step.
 */
function flatten(timeline) {
    const out = [];
    for (const item of timeline) {
        if (item.kind === "thinking") {
            out.push({ text: item.text, kind: "thinking" });
            continue;
        }
        if (item.kind === "step") {
            out.push({ text: item.title, kind: "step", status: item.status });
            for (const t of item.tools || []) {
                const text = simplify(t.label);
                out.push({ text, kind: "tool", tool: t, status: "done" });
            }
        }
    }
    return out;
}

/**
 * Encurta o label do tool para uma frase legível.
 * Ex.: "Searching for \`abc def\`" -> "Pesquisando abc def"
 */
function simplify(label = "") {
    const m = /^([A-Za-z ]+?)(?:\s+for)?\s+`([^`]+)`/.exec(label);
    if (!m) return label;
    const actionRaw = m[1].trim().toLowerCase();
    const arg = m[2];
    const mapped = {
        "searching": "Pesquisando",
        "searching images": "Buscando imagens",
        "creating file": "Criando arquivo",
        "adding content to file": "Editando arquivo",
        "editing file": "Editando arquivo",
        "executed command": "Executando",
        "executing command": "Executando",
        "opening": "Abrindo",
    };
    const action = mapped[actionRaw] || capitalize(actionRaw);
    const shortArg = arg.length > 80 ? arg.slice(0, 77) + "..." : arg;
    return `${action} ${shortArg}`;
}

function capitalize(s) {
    return s ? s[0].toUpperCase() + s.slice(1) : s;
}

function parseToolLabel(label = "") {
    const a = label.toLowerCase();
    if (a.includes("searching images")) return { Icon: ImageIcon, color: "#EC4899" };
    if (a.includes("search")) return { Icon: Search, color: "#3B82F6" };
    if (a.includes("creating file") || a.includes("adding content") || a.includes("editing file")) return { Icon: FileCode2, color: "#A855F7" };
    if (a.includes("executed command") || a.includes("executing")) return { Icon: Terminal, color: "#22C55E" };
    if (a.includes("browser") || a.includes("opening")) return { Icon: Link2, color: "#F97316" };
    if (a.includes("pdf")) return { Icon: FileText, color: "#EF4444" };
    return { Icon: Wrench, color: "#94A3B8" };
}
